import { useMemo, useState } from "react";
import { Link } from "react-router";
import { Alert, Segmented, Table, Tag, Tooltip } from "antd";

import { useFetch } from "@/hooks/FetchHook";
import { useScopeParams } from "@/hooks/useScopeParams";
import Loader from "@/components/UI/Loader";
import Error from "@/components/UI/Error";
import { designFormToInstanceActivity } from "./activityCrosswalk";

/**
 * Study-design "Schedule" — a compact list of the activities the protocol design defines, with a
 * drill-through from each clinical activity to its actual completions in Visits & Activities.
 *
 * This deliberately does NOT render a visit × activity grid: the Apollo SoA as loaded is a full
 * cross-product (every clinical activity marked at every visit), so a grid is a wall of identical
 * checkmarks that says nothing. Until ingestion delivers a genuinely sparse per-visit schedule, the
 * honest, useful view is: here are the design's activities, and here's where each one's actuals live.
 *
 * Sources are segregated — Apollo = clinical assessments (shown first), ODM = eCOA/system forms.
 * Only clinical activities that resolve to a real instance activity (via the crosswalk, trial-scoped)
 * become links; system forms and no-match rows stay plain text (no dead links).
 */

type SoaRow = {
  visit_name: string | null;
  form_name: string | null;
  is_expected: boolean | null;
  design_source: string | null;
};

type PaginatedSoa = { results?: SoaRow[] } | SoaRow[] | undefined;

export default function StudyDesignSchedule() {
  const { scope, id } = useScopeParams();

  const { data, isLoading, isError } = useFetch({
    endpointSuffix: "filters/data/",
    method: "POST",
    body: {
      dataset_type: "study_design_soa",
      pagination: { page: 1, page_size: 2000 },
    },
  });

  const { data: activityDict } = useFetch({ endpointSuffix: "activities/" });
  const instanceActivities: string[] = useMemo(() => {
    const dict = activityDict as Record<string, string[]>[] | undefined;
    if (!Array.isArray(dict)) return [];
    return dict.flatMap((entry) => Object.values(entry).flat());
  }, [activityDict]);

  const results: SoaRow[] = useMemo(() => {
    const d = data as PaginatedSoa;
    if (!d) return [];
    return Array.isArray(d) ? d : (d.results ?? []);
  }, [data]);

  const sources = useMemo(() => {
    const present = Array.from(
      new Set(
        results.map((r) => r.design_source).filter((s): s is string => !!s),
      ),
    );
    return present.sort((a, b) =>
      a === "Apollo" ? -1 : b === "Apollo" ? 1 : a.localeCompare(b),
    );
  }, [results]);

  const [source, setSource] = useState<string | null>(null);
  const activeSource = source ?? sources[0] ?? null;

  // One row per distinct activity/form in the selected source, with the count of visits it is
  // scheduled at and its instance-activity drill-through target (if any).
  const rows = useMemo(() => {
    if (!activeSource) return [];
    const byForm = new Map<string, Set<string>>();
    results
      .filter(
        (r) =>
          (r.design_source ?? "") === activeSource && r.is_expected !== false,
      )
      .forEach((r) => {
        const form = r.form_name ?? "—";
        if (!byForm.has(form)) byForm.set(form, new Set());
        byForm.get(form)!.add(r.visit_name ?? "—");
      });
    return Array.from(byForm.entries())
      .map(([form, visits]) => ({
        key: form,
        activity: form,
        visitCount: visits.size,
        instanceActivity: designFormToInstanceActivity(
          form,
          instanceActivities,
        ),
      }))
      .sort((a, b) => a.activity.localeCompare(b.activity));
  }, [results, activeSource, instanceActivities]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <Loader />
      </div>
    );
  }
  if (isError) return <Error />;

  const columns = [
    {
      title: "Activity / Form",
      dataIndex: "activity",
      key: "activity",
      render: (value: string, row: (typeof rows)[number]) => {
        // Prefer the friendly instance name (e.g. "EQ-5D-5L") over the packed design code
        // ("EQ5D5LT"); keep the raw code muted alongside for provenance.
        const label = row.instanceActivity ?? value;
        const showCode =
          row.instanceActivity != null && row.instanceActivity !== value;
        return (
          <span className="flex items-baseline gap-2">
            {row.instanceActivity ? (
              <Tooltip
                title={`View "${row.instanceActivity}" completions in Visits & Activities`}
              >
                <Link
                  className="font-medium"
                  style={{ color: "#06709B" }}
                  to={`/${scope}/${id}/subjects/visits-activities?activity=${encodeURIComponent(
                    row.instanceActivity,
                  )}`}
                >
                  {label}
                </Link>
              </Tooltip>
            ) : (
              <span className="font-medium">{label}</span>
            )}
            {showCode && (
              <span className="text-gray-400 text-[12px]">{value}</span>
            )}
          </span>
        );
      },
    },
    {
      title: "Drill-through",
      dataIndex: "instanceActivity",
      key: "link",
      width: 160,
      render: (act: string | null) =>
        act ? (
          <Tag color="blue">Visits &amp; Activities</Tag>
        ) : (
          <span className="text-gray-400 text-[12px]">—</span>
        ),
    },
    {
      title: "Scheduled at",
      dataIndex: "visitCount",
      key: "visitCount",
      width: 130,
      render: (n: number) => `${n} visit${n === 1 ? "" : "s"}`,
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        {sources.length > 1 && (
          <Segmented
            value={activeSource ?? undefined}
            onChange={(val) => setSource(val as string)}
            options={sources.map((s) => ({
              label: s === "Apollo" ? "Clinical (Apollo)" : s,
              value: s,
            }))}
          />
        )}
        {activeSource && (
          <span className="text-[13px] text-gray-500">
            {rows.length} activities
          </span>
        )}
      </div>

      {results.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="No study-design schedule available for this trial."
        />
      ) : (
        <Table
          className="shadow-sm"
          columns={columns}
          dataSource={rows}
          pagination={false}
          size="small"
          scroll={{ y: window.innerHeight - 340 }}
        />
      )}
    </div>
  );
}
